import { subMonths, format, parse } from 'tz-date-fns'

import { ArrayUtils, DateFormat, getFormattedDate } from '@shopping/common'
import { StateKey } from 'common/utils/flipState'
import { SubVertical } from 'common/constants'
import { ProductCategory } from 'common/types/entities/mongo/productCategory'
import { Image } from 'common/types/entities/mongo/sub'
import { MenuType } from 'common/constants/menu'
import { DisplayMenu, ROOT_ID } from 'common/types/entities/mongo/menu'
import { fetchBy } from 'common/services/finder/menu'
import { fetchKeywordByMenuId } from 'common/services/finder/menuAndKeyword'
import { fetchProductCategories } from 'common/services/productCategory'
import { DisplayType } from 'common/ducks/menu'
import { fetchTemplateInstances } from 'common/services/template'
import { Template } from 'common/types/entities/mongo/template'
import { TemplateManagerKey } from 'common/constants/templateManager'

export interface Banner {
  pcTitle1?: string
  pcTitle2?: string
  pcImage?: Image
  mobileTitle1?: string
  mobileTitle2?: string
  mobileImage?: Image
}

export interface MenuByKeyword {
  menu: DisplayMenu[]
  menuProductCategory: {
    [key: string]: ProductCategory[]
  }
  month: string
  productCategoryId: string
  productCategory: ProductCategory
  bannerLoad: boolean
  banner?: Banner
  beforeCurrentAfterMonth: { [key: string]: { full: string; short: string } }
  firstHarvestBanner?: any
}

const initialState: MenuByKeyword = {
  menu: [],
  menuProductCategory: {},
  month: '',
  productCategoryId: '',
  productCategory: null,
  bannerLoad: false,
  banner: null,
  beforeCurrentAfterMonth: null,
  firstHarvestBanner: null,
}

const INIT = 'directfarm/menuByKeywords/INIT'
const SET_MONTH = 'directfarm/menuByKeywords/SET_MONTH'
const SET_PRODUCT_CATEGORY_ID = 'directfarm/menuByKeywords/SET_PRODUCT_CATEGORY_ID'
const SET_BANNER_LOAD = 'directfarm/menuByKeywords/SET_BANNER_LOAD'

let DEFAULT_BANNER = {
  pcTitle2: '신선한 제철상품을  \n 만나보세요',
  pcImage: {
    imageUrl: 'https://shop-phinf.pstatic.net/20190321_194/shopping_1553166443495fGyt1_GIF/%B0%AB_pc.gif',
  },
  mobileTitle2: '신선한 제철상품을  \n 만나보세요',
  mobileImage: {
    imageUrl: 'https://shop-phinf.pstatic.net/20190321_212/shopping_1553166451771EbVTR_GIF/%B0%AB.gif',
  },
}

const getBeforeCurrentAfterMonth = () => {
  const currentDate = new Date()
  const ifn = [1, 0, -1]
  return ifn.reduce((o, i) => {
    const month = subMonths(currentDate, i)
    return {
      ...o,
      [`${getFormattedDate(month, 'M')}월`]: {
        full: format(parse(month), 'MMMM').toLocaleLowerCase(),
        short: format(parse(month), 'MMM'),
      },
    }
  }, {})
}

export const initMenuByKeywords = (
  curState: StateKey,
  subVertical: SubVertical,
  queryParams?: any,
) => async dispatch => {
  const menuTypes = [MenuType.SEASONAL_FOOD]
  const seasonalFoodMenu: DisplayMenu[] = await fetchBy(subVertical, ROOT_ID, DisplayType.NO_RULE, menuTypes)
  const seasonalFoodFirstMenu = ArrayUtils.getFirst(seasonalFoodMenu)
  const firstDisplayMenus: DisplayMenu[] = await fetchBy(
    subVertical,
    seasonalFoodFirstMenu.id,
    DisplayType.VERTICAL_HOME,
    menuTypes,
  )

  const beforeCurrentAfterMonth = getBeforeCurrentAfterMonth()
  const validMonths = Object.keys(beforeCurrentAfterMonth)
  const monthDisplayMenus = validMonths.map(month => firstDisplayMenus.find(menu => menu.name === month))

  const menuAndKeywords = await Promise.all(monthDisplayMenus.map(menu => fetchKeywordByMenuId(menu.id)))

  const productCategoryIds = menuAndKeywords.reduce((acc, menuAndKeyword) => {
    return [...acc, ...menuAndKeyword.productCategories.map(item => item._id)]
  }, [])
  const categories = await fetchProductCategories({ categories: productCategoryIds })

  const menuProductCategory = monthDisplayMenus.reduce((acc, displayMenu) => {
    const menuAndKeyword = menuAndKeywords.find(item => item._id === displayMenu.id)
    if (!menuAndKeyword) return acc

    return {
      ...acc,
      [displayMenu.name]: [
        {
          _id: 'ALL',
          name: '전체',
        },
        ...menuAndKeyword.productCategories.map(item => {
          return categories.find(category => category._id === item._id)
        }),
      ],
    }
  }, {})

  // 초기화.
  const { month, productCategoryId } = queryParams

  const currentMonth = month ? `${month}월` : validMonths[1]
  const productCategory =
    productCategoryId && productCategoryId !== 'ALL'
      ? menuProductCategory[currentMonth].find(category => category._id === productCategoryId)
      : menuProductCategory[currentMonth][0]

  // banner
  const template: Template[] = await fetchTemplateInstances(TemplateManagerKey.DIRECT_FARM_HOME_ALL, {})
  const bannerData = (ArrayUtils.isNotEmpty(template) && template[0].data) || null
  if (bannerData) {
    DEFAULT_BANNER = {
      pcTitle2: bannerData.title ? bannerData.title.value : DEFAULT_BANNER.pcTitle2,
      pcImage: {
        imageUrl: bannerData.pcImage ? bannerData.pcImage.value : DEFAULT_BANNER.pcImage.imageUrl,
      },
      mobileTitle2: bannerData.title ? bannerData.title.value : DEFAULT_BANNER.mobileTitle2,
      mobileImage: {
        imageUrl: bannerData.mobileImage ? bannerData.mobileImage.value : DEFAULT_BANNER.mobileImage.imageUrl,
      },
    }
  }

  // firstHarvestBanner
  const firstHarvestBannerTemplate: Template[] = await fetchTemplateInstances(
    TemplateManagerKey.DIRECT_FARM_HOME_BANNER,
    {},
  )
  const firstHarvestBanner =
    (ArrayUtils.isNotEmpty(firstHarvestBannerTemplate) && firstHarvestBannerTemplate[0].data) || null

  await dispatch({
    curState,
    result: {
      menuProductCategory,
      productCategory,
      beforeCurrentAfterMonth,
      firstHarvestBanner,
      banner: productCategory.banner || DEFAULT_BANNER,
      menu: monthDisplayMenus,
      productCategoryId: productCategory._id,
      month: currentMonth,
    },
    type: INIT,
  })
}

export const setMonth = (curState: StateKey, month: string) => async (dispatch, getState) => {
  const { menuProductCategory } = getState().menuByKeyword[curState]

  const productCategory = (ArrayUtils.isNotEmpty(menuProductCategory[month]) && menuProductCategory[month][0]) || null
  const productCategoryId = (productCategory && productCategory._id) || ''

  await dispatch({
    curState,
    result: {
      bannerLoad: true,
    },
    type: SET_BANNER_LOAD,
  })

  await dispatch({
    curState,
    result: {
      month,
      productCategoryId,
      productCategory,
      banner: DEFAULT_BANNER,
      bannerLoad: false,
    },
    type: SET_MONTH,
  })
}

export const setProductCategoryId = (curState: StateKey, productCategoryId: string) => async (dispatch, getState) => {
  const { menuProductCategory, month, productCategory } = getState().menuByKeyword[curState]

  if (productCategory._id === productCategoryId) return
  const foundProductCategory = menuProductCategory[month].find(pc => pc._id === productCategoryId) || null

  await dispatch({
    curState,
    result: {
      bannerLoad: true,
    },
    type: SET_BANNER_LOAD,
  })

  const banner = foundProductCategory.banner || DEFAULT_BANNER

  await dispatch({
    curState,
    result: {
      productCategoryId,
      banner,
      productCategory: foundProductCategory,
      bannerLoad: false,
    },
    type: SET_PRODUCT_CATEGORY_ID,
  })
}

export const initBanner = (curState: StateKey) => async (dispatch, getState) => {
  await dispatch({
    curState,
    result: {
      bannerLoad: false,
    },
    type: SET_BANNER_LOAD,
  })
}

// reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case INIT:
    case SET_MONTH:
    case SET_PRODUCT_CATEGORY_ID:
    case SET_BANNER_LOAD:
      return {
        ...state,
        ...action.result,
      }
    default:
      return state
  }
}
